import { useRef, useCallback } from 'react';
import fetchRequest from '../utils/fetchRequest';
import { delay } from '../utils/timers';

export function useReactivation({ noty, refetch }) {
  const isRunning = useRef(false);

  const waitForTheReactivation = useCallback(async (subId) => {
    try {
      const MAX_RETRIES = 120;
      const waitTime = 5000;
      let count = 0;

      await delay(waitTime);
      while (count < MAX_RETRIES) {
        const response = await fetchRequest.get(
          `/subscriptions/${subId}/reactivation_status`,
        );
        const { status, errorType } = response.data;

        switch (status) {
          case 'success':
            return { status: 'success' };
          case 'pending':
            count++;
            await delay(waitTime);
            break;
          case 'failed':
            return { status: 'failed', errorType };
          case 'not_found':
          default:
            return { status: 'not_found' };
        }
      }
      return { status: 'no_info' };
    } catch {
      return { status: 'failed' };
    }
  }, []);

  const handleReactivation = useCallback(
    async (subId) => {
      if (isRunning.current) return;
      isRunning.current = true;

      try {
        const res = await waitForTheReactivation(subId);

        switch (res.status) {
          case 'success':
            noty.success({
              value: 'Subscription is successfully reactivated',
              disableAutoClose: true,
            });
            refetch();
            break;
          case 'failed':
            if (res?.errorType === 'payment') {
              noty.error({
                value:
                  'Failed to reactivate subscription due to payment issue. Please try again',
                disableAutoClose: true,
              });
            } else {
              noty.error({
                value:
                  'Failed to reactivate subscription due to internal issue. Please contact support',
                disableAutoClose: true,
              });
            }
            break;
          default:
            break;
        }

        return res;
      } finally {
        isRunning.current = false;
      }
    },
    [waitForTheReactivation, noty, refetch],
  );

  return { handleReactivation };
}
